import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import useDebounce from "../../hooks/useDebounce";
import AppBar from "../../common/AppBar";
import AppTable from "../../common/AppTable/AppTable";
import Loader from "../../common/Loader";
import Snackbar from "../../common/Snackbar";
import { fetchUsers, deleteUser, exportUsersCSV } from "../../redux/userSlice";
import { getAllModules } from "../../redux/moduleSlice";
import { Button, TableActionGroup } from "../../common/Buttons";
import DeleteConfirmation from "../../common/Confirmations/DeleteConfirmation";
import { buildPermissionContext } from "../../util/permissions";

const columns = [
  {
    headName: "User Name",
    field: "email",
    sort: true,
    filter: true,
    width: "200px",
  },
  {
    headName: "Name",
    field: "name",
    sort: true,
    filter: true,
    width: "150px",
  },
  {
    headName: "Module Permissions (Live Mode)",
    field: "module_type_permissions_view",
    columnKey: "module_permissions_live_mode", // Unique key for column selection
    sort: true,
    filter: true,
    width: "200px",
  },
  {
    headName: "Module Permissions (Edit Mode)",
    field: "module_type_permissions_edit",
    columnKey: "module_permissions_edit_mode", // Unique key for column selection
    sort: true,
    filter: true,
    width: "200px",
  },
  {
    headName: "Record Permissions In Live Mode",
    field: "livemode_permissions_to_edit",
    sort: true,
    filter: true,
    width: "200px",
  },
  {
    headName: "Edit Mode Permissions",
    field: "permission_to_edit",
    sort: true,
    filter: true,
    width: "150px",
  },
  {
    headName: "Role",
    field: "role",
    sort: true,
    filter: true,
    width: "150px",
  },
  {
    headName: "Active Status",
    field: "status",
    width: "100px",
    sort: true,
    filter: true,
  },
  {
    headName: "User Information",
    field: "user_information",
    sort: true,
    filter: true,
    width: "200px",
    hidden: true, // Hide from table display but keep in select columns filter
  },
  {
    headName: "Notes About User",
    field: "note",
    sort: true,
    filter: true,
    width: "200px",
    hidden: true, // Hide from table display but keep in select columns filter
  },
  { headName: "Action", field: "action", isAction:true },
];

const Users = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { users, loading, totalUsers } = useSelector((state) => state.users);
  const { modules } = useSelector((state) => state.module);
  const authUser = useSelector((state) => state.auth.user);
  const permissionCtx = buildPermissionContext(authUser);
  const canEdit = permissionCtx.permissions?.Edit === true;

  const [visibleColumns, setVisibleColumns] = useState(columns);

  // Filter out hidden columns for calculating colSpan
  const visibleColumnsCount = useMemo(() => {
    return columns.filter((col) => !col.hidden).length;
  }, []);

  // Initialize limit from localStorage to persist user preference
  const [limit, setLimit] = useState(() => {
    const saved = localStorage.getItem("appTableLimit");
    return saved ? parseInt(saved) : 10;
  });
  const [offset, setOffset] = useState(() => {
    const saved = localStorage.getItem("appTableOffset");
    return saved ? parseInt(saved) : 0;
  });
  const [currentApiQuery, setCurrentApiQuery] = useState(
    `limit=10&offset=0&order_by=name&order=desc`
  );
  const [localLoading, setLocalLoading] = useState(false);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    type: "",
  });

  const showSnackbar = (message, type) => {
    setSnackbar({ open: true, message, type });
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleFilterSort = useCallback(
    (queryString) => {
      if (!queryString) {
        // Set default sort: order_by=name&order=DESC
        const defaultQuery = `limit=${limit}&offset=${offset}&order_by=name&order=desc`;
        setCurrentApiQuery(defaultQuery);
        return;
      }

      // Parse the query string properly
      const params = new URLSearchParams(queryString);

      // Build new query string with all parameters
      const newParams = new URLSearchParams();

      // Copy all existing parameters except sort_by and sort_order
      for (const [key, value] of params.entries()) {
        if (key !== "sort_by" && key !== "sort_order") {
          newParams.append(key, value);
        }
      }

      // Handle sort parameters: convert sort_by/sort_order to order_by/order
      if (params.has("sort_by")) {
        const sortBy = params.get("sort_by");
        const sortOrder = (params.get("sort_order") || "asc").toLowerCase(); // Ensure lowercase
        newParams.append("order_by", sortBy);
        newParams.append("order", sortOrder);
      } else if (!params.has("order_by")) {
        // If no sort is specified, add default sort
        newParams.append("order_by", "name");
        newParams.append("order", "desc");
      } else {
        // Keep existing order_by and order if they exist
        newParams.append("order_by", params.get("order_by"));
        newParams.append("order", (params.get("order") || "asc").toLowerCase()); // Ensure lowercase
      }

      const transformedQuery = newParams.toString();

      if (transformedQuery !== currentApiQuery) {
        setCurrentApiQuery(transformedQuery);
      }
    },
    [currentApiQuery, limit, offset]
  );

  // Wrapper functions to update limit and offset with localStorage persistence
  const setLimitWithPersistence = useCallback((newLimit) => {
    setLimit(newLimit);
    localStorage.setItem("appTableLimit", newLimit.toString());
    // Reset offset to 0 when limit changes
    setOffset(0);
    localStorage.setItem("appTableOffset", "0");
  }, []);

  const setOffsetWithPersistence = useCallback((newOffset) => {
    setOffset(newOffset);
    localStorage.setItem("appTableOffset", newOffset.toString());
  }, []);

  // Compute pagination metadata with totalPages
  const computedPagination = useMemo(() => {
    const currentLimit = limit;
    const currentOffset = offset;
    const total = totalUsers || 0;

    // Calculate totalPages based on limit and total
    const totalPages = currentLimit > 0 ? Math.ceil(total / currentLimit) : 1;

    return {
      limit: currentLimit,
      offset: currentOffset,
      total,
      totalPages,
    };
  }, [limit, offset, totalUsers]);

  // Fetch modules to ensure type filter labels are available in UserForm
  useEffect(() => {
    dispatch(getAllModules({ limit: 1000, offset: 0 }));
  }, [dispatch]);

  // Sync limit and offset from localStorage on mount
  useEffect(() => {
    const savedLimit = localStorage.getItem("appTableLimit");
    const savedOffset = localStorage.getItem("appTableOffset");

    if (savedLimit) {
      setLimit(parseInt(savedLimit));
    }
    if (savedOffset) {
      setOffset(parseInt(savedOffset));
    }
  }, []);

  // Get valid module names from modules API response
  const validModuleNames = useMemo(() => {
    if (!modules || !Array.isArray(modules)) return [];
    const moduleNames = new Set();
    modules.forEach((module) => {
      if (module?.module_part?.type_name) {
        moduleNames.add(module.module_part.type_name);
      }
    });
    return Array.from(moduleNames);
  }, [modules]);

  useEffect(() => {
    dispatch(
      fetchUsers({
        limit,
        offset,
        searchQuery: currentApiQuery || "",
        moduleNames: validModuleNames,
      })
    );
  }, [dispatch, limit, offset, currentApiQuery, validModuleNames]);

  // Helper function to render Live Mode permissions
  const renderLiveModePermissions = (
    moduleTypePermissionsView,
    moduleTypePermissions
  ) => {
    // Support both new format (moduleTypePermissionsView) and old format (moduleTypePermissions) for backward compatibility
    let viewPermissions = {};

    if (
      moduleTypePermissionsView &&
      typeof moduleTypePermissionsView === "object"
    ) {
      // New format - already separated
      viewPermissions = moduleTypePermissionsView;
    } else if (
      moduleTypePermissions &&
      typeof moduleTypePermissions === "object"
    ) {
      // Old format - convert from nested structure
      Object.entries(moduleTypePermissions).forEach(([module, perms]) => {
        if (perms && typeof perms === "object" && perms.view === true) {
          viewPermissions[module] = true;
        }
      });
    }

    if (!viewPermissions || Object.keys(viewPermissions).length === 0) {
      return "None";
    }

    const filteredPermissions = Object.entries(viewPermissions).filter(
      ([module]) =>
        module === "Not Assigned" ||
        (validModuleNames.length > 0 ? validModuleNames.includes(module) : true)
    );

    if (filteredPermissions.length === 0) {
      return "None";
    }

    // Filter only modules with view permission (value is true)
    const viewModules = filteredPermissions.filter(
      ([_, hasPermission]) => hasPermission === true
    );

    if (viewModules.length === 0) {
      return "None";
    }

    // Check if user has view permission for all valid modules (excluding "Not Assigned")
    const modulesToCheck = validModuleNames.filter(
      (name) => name !== "Not Assigned"
    );
    const hasAllModules =
      modulesToCheck.length > 0 &&
      modulesToCheck.every((moduleName) => {
        return viewPermissions[moduleName] === true;
      });

    // Show "All Modules" if user has view permission for all valid modules
    if (hasAllModules) {
      return "All Modules";
    }

    // Show individual modules with view permission as comma-separated
    return viewModules.map(([module]) => module).join(", ");
  };

  // Helper function to render Edit Mode permissions
  const renderEditModePermissions = (
    moduleTypePermissionsEdit,
    moduleTypePermissions
  ) => {
    // Support both new format (moduleTypePermissionsEdit) and old format (moduleTypePermissions) for backward compatibility
    let editPermissions = {};

    if (
      moduleTypePermissionsEdit &&
      typeof moduleTypePermissionsEdit === "object"
    ) {
      // New format - already separated
      editPermissions = moduleTypePermissionsEdit;
    } else if (
      moduleTypePermissions &&
      typeof moduleTypePermissions === "object"
    ) {
      // Old format - convert from nested structure
      Object.entries(moduleTypePermissions).forEach(([module, perms]) => {
        if (perms && typeof perms === "object" && perms.edit === true) {
          editPermissions[module] = true;
        }
      });
    }

    if (!editPermissions || Object.keys(editPermissions).length === 0) {
      return "None";
    }

    const filteredPermissions = Object.entries(editPermissions).filter(
      ([module]) =>
        module === "Not Assigned" ||
        (validModuleNames.length > 0 ? validModuleNames.includes(module) : true)
    );

    if (filteredPermissions.length === 0) {
      return "None";
    }

    // Filter only modules with edit permission (value is true)
    const editModules = filteredPermissions.filter(
      ([_, hasPermission]) => hasPermission === true
    );

    if (editModules.length === 0) {
      return "None";
    }

    // Check if user has edit permission for all valid modules (excluding "Not Assigned")
    const modulesToCheck = validModuleNames.filter(
      (name) => name !== "Not Assigned"
    );
    const hasAllModules =
      modulesToCheck.length > 0 &&
      modulesToCheck.every((moduleName) => {
        return editPermissions[moduleName] === true;
      });

    // Show "All Modules" if user has edit permission for all valid modules
    if (hasAllModules) {
      return "All Modules";
    }

    // Show individual modules with edit permission as comma-separated
    return editModules.map(([module]) => module).join(", ");
  };

  // Helper function to render Record Permissions In Live Mode
  const renderRecordPermissions = (livemodePermissions) => {
    if (!livemodePermissions || typeof livemodePermissions !== "object") {
      return "None";
    }

    const allPermissionKeys = [
      "View",
      "Edit",
      "Create",
      "Submit",
      "Approve",
      "Release",
      "Archive",
      "Reject",
      "Reassign",
      "Import/Export",
    ];

    // Get all permissions that are true
    const truePermissions = Object.entries(livemodePermissions)
      .filter(([_, value]) => value === true)
      .map(([key]) => key);

    if (truePermissions.length === 0) {
      return "None";
    }

    // Check if all permission keys in the object are true
    const allKeysInObject = Object.keys(livemodePermissions);
    const allTrue = allKeysInObject.every(
      (key) => livemodePermissions[key] === true
    );

    // If all permissions are true, show "All"
    if (allTrue && allKeysInObject.length > 0) {
      return "All";
    }

    return truePermissions.join(", ");
  };

  // Helper function to render Role categories
  const renderRoles = (roleCategories) => {
    if (
      !roleCategories ||
      !Array.isArray(roleCategories) ||
      roleCategories.length === 0
    ) {
      return "None";
    }

    const roleNames = roleCategories
      .map((role) => role.category_name)
      .filter(Boolean);

    if (roleNames.length === 0) {
      return "None";
    }

    return roleNames.join(", ");
  };

  const toggleColumn = (col) => {
    setVisibleColumns((prev) =>
      prev.includes(col) ? prev.filter((c) => c !== col) : [...prev, col]
    );
  };

  const handleSaveClick = () => {
    navigate("/add-user");
  };

  const handleDelete = async (userId) => {
    try {
      setIsActionLoading(true);
      await dispatch(deleteUser(userId)).unwrap();

      // If we're on a page with only one item and we delete it, go to previous page
      const currentPageItemCount = users?.length || 0;
      const shouldGoToPreviousPage = currentPageItemCount === 1 && offset > 0;
      let effectiveOffset = offset;

      if (shouldGoToPreviousPage) {
        const newOffset = Math.max(0, offset - limit);
        effectiveOffset = newOffset;
        setOffsetWithPersistence(newOffset);
      }

      // Refresh the users list after successful deletion
      await dispatch(
        fetchUsers({
          limit,
          offset: effectiveOffset,
          searchQuery: currentApiQuery || "",
          moduleNames: validModuleNames,
        })
      );

      showSnackbar("User deleted successfully.", "success");
    } catch (error) {
      // Extract error message from the rejected value
      // When using rejectWithValue, the error is the value passed to rejectWithValue
      const errorMessage =
        (typeof error === "string" ? error : null) ||
        error?.error ||
        error?.message ||
        "Failed to delete user. This user may be referenced in other records.";
      showSnackbar(errorMessage, "error");
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleDeleteClick = (userId, userName) => {
    DeleteConfirmation(
      () => handleDelete(userId),
      `Are you sure you want to delete user "${userName}"? This action cannot be undone.`,
      "Delete User"
    );
  };

  const handleExportCSV = async () => {
    await dispatch(exportUsersCSV(validModuleNames));
  };

  const contentAfterHeader = (
    <div
      style={{ textAlign: "right", marginTop: "-30px", marginBottom: "8px" }}
    >
      <Button onClick={handleSaveClick} size="sm">
        Add New User
      </Button>
    </div>
  );

  const paginationLeftContent = (
    <button
      className="appbar-buttonpopup"
      style={{
        border: "1px solid #0D6EFD",
        backgroundColor: "#ffffff",
        color: "#0D6EFD",
        height: "40px",
        fontSize: "13px",
        marginLeft: "-1px",
        marginTop: "10px",
        opacity: canEdit ? 1 : 0.5,
        cursor: canEdit ? "pointer" : "not-allowed",
      }}
      onClick={() => {
        if (!canEdit) return;
        handleExportCSV();
      }}
      disabled={!canEdit}
    >
      Export CSV
    </button>
  );

  return (
    <div>
      <AppBar
        title="Users"
        showBlackTitle={true}
        showTablesDropdown={false}
        buttons={[]}
      />

      <AppTable
        data={users}
        loading={loading || localLoading}
        total={totalUsers}
        limit={computedPagination.limit}
        offset={computedPagination.offset}
        columnsData={columns}
        showSearchInput={true}
        paginate={true}
        pagination={{
          limit: [computedPagination.limit, setLimitWithPersistence],
          offset: [computedPagination.offset, setOffsetWithPersistence],
          pagination: computedPagination,
          paginationPosition: {
            position: "right",
            leftContent: paginationLeftContent,
          },
        }}
        extrapadding="0px"
        onFilterSortHandler={handleFilterSort}
        onRowClick={(row) => {
          navigate("/add-user", {
            state: { editMode: true, user: row },
          });
        }}
        contentAfterHeader={contentAfterHeader}
        enableLocalStoragePersistence={true}
      >
        {loading || localLoading ? (
          <tr>
            <td
              colSpan={visibleColumnsCount}
              style={{ textAlign: "center", padding: "20px" }}
            >
              Loading...
            </td>
          </tr>
        ) : users && users.length > 0 ? (
          users.map((row, index) => (
            <tr key={index}>
              {/* Email */}
              <td>{row.email || "None"}</td>

              {/* Full Name */}
              <td>
                {row.first_name || row.last_name
                  ? `${row.first_name || ""} ${row.last_name || ""}`.trim()
                  : "None"}
              </td>

              {/* Module Permissions (Live Mode) */}
              <td>
                {renderLiveModePermissions(
                  row.module_type_permissions_view,
                  row.module_type_permissions
                )}
              </td>

              {/* Module Permissions (Edit Mode) */}
              <td>
                {renderEditModePermissions(
                  row.module_type_permissions_edit,
                  row.module_type_permissions
                )}
              </td>

              {/* Record Permissions In Live Mode */}
              <td>
                {renderRecordPermissions(row.livemode_permissions_to_edit)}
              </td>

              {/* Edit Mode Permissions - Displayed as List */}
              <td>
                {row.permission_to_edit &&
                Object.values(row.permission_to_edit).some(Boolean)
                  ? row.permission_to_edit.SuperAdmin === true
                    ? "SuperAdmin"
                    : row.permission_to_edit.Admin === true
                    ? "Admin"
                    : Object.entries(row.permission_to_edit)
                        .filter(([_, value]) => value === true)
                        .map(([key]) => key)
                        .join(", ")
                  : "None"}
              </td>

              {/* Role */}
              <td>{renderRoles(row.role_categories)}</td>

              {/* Status */}
              <td>
                {row.status
                  ? row.status.charAt(0).toUpperCase() + row.status.slice(1)
                  : "None"}
              </td>

              {/* Action Buttons */}
              <td>
                <TableActionGroup
                  mode="view"
                  onEdit={(e) => {
                    e.stopPropagation();
                    navigate("/add-user", {
                      state: { editMode: true, user: row },
                    });
                  }}
                  onDelete={(e) => {
                    e.stopPropagation();
                    const userId = row.id || row.user_id;
                    const userName =
                      row.email ||
                      `${row.first_name} ${row.last_name}`.trim() ||
                      "this user";
                    handleDeleteClick(userId, userName);
                  }}
                  disableEdit={row.email === "system@task.com"}
                  disableDelete={
                    row.is_login === true ||
                    row.is_trainer === true ||
                    row.is_trainee === true ||
                    row.is_in_procedure_revision_required_by === true ||
                    row.is_in_task === true ||
                    row.email === "system@task.com"
                  }
                />
              </td>
            </tr>
          ))
        ) : (
          <tr>
            <td
              colSpan={visibleColumnsCount}
              style={{ textAlign: "center", padding: "20px" }}
            >
              No records found
            </td>
          </tr>
        )}
      </AppTable>

      {isActionLoading && <Loader text="Processing..." />}
      {snackbar.open && (
        <Snackbar
          message={snackbar.message}
          type={snackbar.type}
          onClose={handleCloseSnackbar}
        />
      )}
    </div>
  );
};

export default Users;
